import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import {
  DIAM_API_PATH,
  DIAM_PAGE_LIMIT,
  DIAM_TEAM_ID,
  DiamPageSortField,
  DiamTeamRoleFilter,
  getTeamRolesPage,
  getTeamsPage,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import {
  buildTeamRolesPageFilter,
  toDiamSortFields,
} from '../../diamGridUtils';
import { TEAMS_LIST_FILTER } from '../TeamsGrid/useTeams';

export const TEAM_ROLES_FILTER: DiamTeamRoleFilter = { type: 'TEAM' };

export interface UseTeamRolesPageParams {
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
}

const DEFAULT_SORT: DiamPageSortField[] = [{ field: 'name', direction: 'ASC' }];

export function useTeamRoles({
  page,
  pageSize,
  sortModel,
  filterModel,
}: UseTeamRolesPageParams) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);
  const filter = buildTeamRolesPageFilter(TEAM_ROLES_FILTER, filterModel);
  const sort = toDiamSortFields(sortModel, DEFAULT_SORT);

  return useQuery({
    queryKey: [
      'diam',
      'teams',
      DIAM_TEAM_ID,
      'roles',
      'page',
      page,
      pageSize,
      sort,
      filter,
    ] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      const [rolesPage, teamsPage] = await Promise.all([
        getTeamRolesPage(apiUrl, idToken, DIAM_TEAM_ID, {
          page,
          limit: pageSize,
          sort,
          filter,
        }),
        getTeamsPage(apiUrl, idToken, {
          page: 0,
          limit: DIAM_PAGE_LIMIT,
          filter: TEAMS_LIST_FILTER,
        }),
      ]);

      const teamNameById = new Map(
        (teamsPage.elements ?? []).map(team => [team.id, team.name]),
      );

      return {
        ...rolesPage,
        teamNameById,
      };
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
